
import React, { useState, useMemo } from 'react';
import { 
  ShieldCheck, LogIn, Smartphone, Monitor, User, ShieldAlert, Database, Search
} from 'lucide-react';
import { User as UserType } from './types';
import { Card, Badge, StatCard } from './SharedComponents';

interface ActivityLog {
  id: string;
  userId: string;
  action: string;
  details: string;
  ip: string;
  timestamp: string;
}

export const ActivityLogPage = ({ users }: { users: UserType[] }) => {
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('TODOS');
  
  const filteredLogs = useMemo(() => {
    const logs: ActivityLog[] = []; // Inicializado vazio por enquanto, mas tipado
    return logs.filter(log => {
      const user = users.find(u => u.id === log.userId);
      const matchesSearch = (user?.name || '').toLowerCase().includes(search.toLowerCase()) || 
                          log.details.toLowerCase().includes(search.toLowerCase());
      const matchesFilter = filterType === 'TODOS' || log.action === filterType;
      return matchesSearch && matchesFilter;
    });
  }, [search, filterType, users]);

  const stats = useMemo(() => ({
    loginsHoje: 0,
    acoesCriticas: 0,
    usuariosAtivos: 0
  }), []);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20 p-4 md:p-8 lg:p-10 text-secondary">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tighter italic leading-none">Monitor de Atividade</h1>
          <p className="text-gray-400 text-sm font-medium italic mt-2">Log de segurança e rastreabilidade de ações em tempo real.</p>
        </div>
        <div className="flex items-center gap-3">
           <Badge className="bg-primary/10 text-primary border-none font-black text-[10px] px-4 py-2 uppercase italic tracking-widest">Auditoria Core Ativa</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard title="Logins Registrados" value={stats.loginsHoje} icon={LogIn} colorClass="bg-blue-50 text-blue-600" />
        <StatCard title="Ações Críticas" value={stats.acoesCriticas} icon={ShieldCheck} colorClass="bg-red-50 text-red-600" />
        <StatCard title="Operadores Sincronizados" value={stats.usuariosAtivos} icon={User} colorClass="bg-emerald-50 text-emerald-600" />
      </div>

      <Card className="bg-white border border-gray-100 shadow-xl rounded-[40px] overflow-hidden">
        <div className="p-8 border-b border-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
           <div className="flex items-center gap-4 w-full md:w-auto">
              <div className="bg-gray-50 p-1.5 rounded-2xl flex items-center gap-1 border border-gray-100 shadow-inner">
                 {['TODOS', 'LOGIN', 'UPDATE', 'APPROVAL', 'SECURITY'].map(type => (
                   <button 
                    key={type}
                    onClick={() => setFilterType(type)}
                    className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${filterType === type ? 'bg-white text-primary shadow-md' : 'text-gray-400 hover:text-secondary'}`}
                   >
                     {type === 'SECURITY' ? 'SEGURANÇA' : type === 'APPROVAL' ? 'APROVAÇÃO' : type}
                   </button>
                 ))}
              </div>
           </div>
           
           <div className="relative w-full md:w-80 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-primary transition-colors" size={18} />
              <input 
                placeholder="Filtrar por usuário ou ação..." 
                className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-transparent rounded-2xl outline-none font-bold text-xs focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
           </div>
        </div>

        <div className="overflow-x-auto scrollbar-hide">
           <table className="w-full text-left border-collapse">
              <thead>
                 <tr className="bg-gray-50/50 text-gray-400 text-[9px] uppercase font-black tracking-[0.2em] border-b border-gray-100">
                    <th className="px-8 py-6">Operador</th>
                    <th className="px-6 py-6">Tipo de Evento</th>
                    <th className="px-6 py-6">Descrição da Ação</th>
                    <th className="px-6 py-6">Origem / IP</th>
                    <th className="px-8 py-6 text-right">Data/Hora</th>
                 </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                 {filteredLogs.map((log) => {
                    const user = users.find(u => u.id === log.userId);
                    return (
                      <tr key={log.id} className="hover:bg-primary/[0.01] transition-colors group">
                        <td className="px-8 py-6">
                           <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center font-black text-slate-400 italic shadow-inner">
                                 {user?.avatar ? <img src={user.avatar} className="w-full h-full object-cover rounded-xl" /> : user?.name.charAt(0)}
                              </div>
                              <div>
                                 <p className="font-black text-secondary uppercase text-xs">{user?.name || 'Sistema'}</p>
                                 <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">{user?.role}</p>
                              </div>
                           </div>
                        </td>
                        <td className="px-6 py-6">
                           <Badge className={
                             log.action === 'LOGIN' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                             log.action === 'APPROVAL' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                             log.action === 'SECURITY' ? 'bg-red-50 text-red-600 border-red-100 animate-pulse' :
                             'bg-gray-50 text-gray-500 border-gray-100'
                           }>
                              {log.action}
                           </Badge>
                        </td>
                        <td className="px-6 py-6">
                           <p className="text-xs font-bold text-slate-600 leading-relaxed max-w-md">{log.details}</p>
                        </td>
                        <td className="px-6 py-6">
                           <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2 text-gray-400 font-bold text-[9px] uppercase">
                                 {log.details.includes('Desktop') ? <Monitor size={10} /> : <Smartphone size={10} />} {log.details.includes('Desktop') ? 'Desktop' : 'Mobile'}
                              </div>
                              <p className="text-[10px] font-mono font-bold text-slate-400">{log.ip}</p>
                           </div>
                        </td>
                        <td className="px-8 py-6 text-right">
                           <div className="flex flex-col items-end">
                              <p className="text-[11px] font-black text-secondary tracking-tight">{new Date(log.timestamp).toLocaleDateString('pt-BR')}</p>
                              <p className="text-[9px] text-gray-400 font-bold uppercase mt-0.5 italic">{new Date(log.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
                           </div>
                        </td>
                      </tr>
                    );
                 })}
              </tbody>
           </table>
           {filteredLogs.length === 0 && (
             <div className="py-32 text-center flex flex-col items-center justify-center gap-4 opacity-20">
                <Database size={64} strokeWidth={1} />
                <p className="text-xs font-black uppercase tracking-[0.4em]">Nenhum log localizado para os termos</p>
             </div>
           )}
        </div>
      </Card>
      
      <div className="p-8 bg-secondary text-white rounded-[48px] shadow-2xl relative overflow-hidden group">
         <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:rotate-12 transition-transform duration-1000"><ShieldAlert size={120} /></div>
         <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="space-y-2 text-center md:text-left">
               <h3 className="text-xl font-black uppercase italic tracking-tight leading-none">Política de Retenção</h3>
               <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.4em]">Os logs de auditoria são mantidos no CORE por 24 meses.</p>
            </div>
            <button className="px-10 py-4 bg-primary text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all">Solicitar Backup Fiscal</button>
         </div>
      </div>
    </div>
  );
};
